import { type FC, useCallback, useEffect, useRef, useState } from "react";
import { Presentation } from "@clientFrontend/features/publication/components/SelectedWorkAlbumImage/Presentation";
import { type WorkContentAlbum } from "@clientFrontend/features/publication/type/type";
import {
  type AlbumCategoryOption,
  type ClientImageResponse,
  type Props,
} from "@clientFrontend/features/publication/components/SelectedWorkAlbumImage/type";
import { type ClientImage } from "@clientFrontend/features/publication/components/ClientImageLibraryModal/type";
import { type OptionProps } from "@root/shared/components/SelectBox";
import { axios } from "@root/libs/axios";
import { sendErrorLog } from "@root/shared/utils/sendErrorLog";
import { type ApiErrorResponse } from "@root/shared/types/type";
import { isApiError } from "@root/shared/utils/typeGuard";

const oldClientImageLibraryImagesCount = () => {
  // ページの中に旧画像ライブラリは複数埋め込まれているので、1つ目の要素を取得
  const firstModalImgThum = document.querySelector(".modal_img-thum");

  if (firstModalImgThum) {
    const items = firstModalImgThum.querySelectorAll(".js-modal-image-list-item-outer");
    return items.length;
  }

  return 0;
};

export const SelectedWorkAlbumImage: FC<Props> = ({
  clientId,
  initialWorkContentAlbums,
  apiUrls,
  planAndImageInfoProps,
  initialAlbumCategoryOptions,
  errorMessages,
}) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  // 本来はClientImageLibraryModalに設定したいが、画像アップロード中にModalを閉じられるとReact側の警告が出るのでここに定義する
  // 警告: 画像アップロード後にClientImageのstateを更新するのだが、Modalが閉じられた(unmount)された状態でstateを更新しようとするとエラーが出る
  const [clientImages, setClientImages] = useState<ClientImage[]>([]);
  const [selectedWorkContentAlbums, setSelectedWorkContentAlbums] =
    useState<WorkContentAlbum[]>(initialWorkContentAlbums);

  // 既にカテゴリが設定済みの場合はinitialAlbumCategoryOptionsの値を受け取る
  const [albumCategoryOptions, setAlbumCategoryOptions] = useState<OptionProps[]>(
    initialAlbumCategoryOptions,
  );
  // 初回読み込み時だけClientImageを取得するためのRef
  const isMounted = useRef(false);

  // 選択肢た求人詳細画像の順番を入れ替える処理
  const handleChangeAlbumSortNo = useCallback(
    (currentSortNo: number, swapSortNo: number) => {
      const newWorkContentAlbums = [...selectedWorkContentAlbums];

      const currentAlbum = newWorkContentAlbums.find((album) => album.sortNo === currentSortNo);
      const swapTargetAlbum = newWorkContentAlbums.find((album) => album.sortNo === swapSortNo);
      if (!currentAlbum || !swapTargetAlbum) return;

      currentAlbum.sortNo = swapSortNo;
      swapTargetAlbum.sortNo = currentSortNo;

      setSelectedWorkContentAlbums(newWorkContentAlbums.sort((a, b) => a.sortNo - b.sortNo));
    },
    [selectedWorkContentAlbums],
  );

  // 画像カテゴリを変更する処理
  const handleChangeAlbumCategory = useCallback(
    (albumCategoryId: number, clientImageId: number) => {
      const newWorkContentAlbum = selectedWorkContentAlbums.find(
        (workContentAlbum) => workContentAlbum.clientImageId === clientImageId,
      );
      if (!newWorkContentAlbum) return;

      newWorkContentAlbum.albumCategoryId = albumCategoryId;
      setSelectedWorkContentAlbums([...selectedWorkContentAlbums]);
    },
    [selectedWorkContentAlbums, setSelectedWorkContentAlbums],
  );

  // 画像モーダルで「画像を設定する」した際に、選択画像を求人アルバム(WorkContentAlbum)へ変換して同期する
  // 既存の設定(id・カテゴリ)は clientImageId をキーに引き継ぎ、sortNo は選択順に振り直す
  const handleConfirmAlbumImages = useCallback(
    (images: ClientImage[]) => {
      const newWorkContentAlbums: WorkContentAlbum[] = images.map((image, index) => {
        const existingAlbum = selectedWorkContentAlbums.find(
          (album) => album.clientImageId === image.id,
        );
        return {
          id: existingAlbum?.id,
          clientImageId: image.id,
          imageType: "album",
          sortNo: index + 1,
          imageUrl: image.imageUrl,
          fileName: image.filename,
          albumCategoryId: existingAlbum?.albumCategoryId,
        };
      });
      setSelectedWorkContentAlbums(newWorkContentAlbums);
    },
    [selectedWorkContentAlbums, setSelectedWorkContentAlbums],
  );

  const cleanAlbumCategory = useCallback(() => {
    const noCategorySelectedWorkContentAlbums = selectedWorkContentAlbums.map((album) => ({
      ...album,
      albumCategoryId: undefined,
    }));
    // FIXME: 職種を変えた時に、ここでselectedWorkContentAlbumsを空にしないとstateは変わるが何故か再レンダリングが行われないので一旦この実装にする
    setSelectedWorkContentAlbums([]);
    setSelectedWorkContentAlbums(noCategorySelectedWorkContentAlbums);
  }, [selectedWorkContentAlbums]);

  // 画像カテゴリをAPIから取得
  const handleChangeAlbumCategoryOptions = useCallback(
    async (businessTypeId: string) => {
      if (!businessTypeId) {
        setAlbumCategoryOptions([]);
        cleanAlbumCategory();
        return;
      }

      try {
        const { data } = await axios.get<AlbumCategoryOption[] | ApiErrorResponse>(
          apiUrls.albumCategoryUrl,
          {
            params: {
              business_type_id: businessTypeId,
              client_id: String(clientId),
            },
          },
        );
        if (isApiError(data)) {
          sendErrorLog(new Error(data.message || "画像カテゴリの取得に失敗しました"));
          return;
        }

        setAlbumCategoryOptions(data);
        // 職種が変わると、名称が同じでもカテゴリのIDは変わるのでクリアする。
        // 職種が変わると、設定する画像やカテゴリも変更する可能性が高いので、クリアする。
        cleanAlbumCategory();
      } catch (error) {
        sendErrorLog(
          new Error(error instanceof Error ? error.message : "画像カテゴリの取得に失敗しました"),
        );
      }
    },
    [apiUrls.albumCategoryUrl, clientId, cleanAlbumCategory, setAlbumCategoryOptions],
  );

  const fetchClientImages = useCallback(async () => {
    if (
      // 以下いずれかの条件に当てはまる場合、ClientImageを取得させない
      // ・画像が初回読み込み済み && 旧画像ライブラリをまだ開いていない(この場合画像の要素数が0になる)
      // ・画像が初回読み込み済み && 旧画像ライブラリの画像数が新画像ライブラリ(このコンポーネント)の画像数に差分がないか、新画像ライブラリの画像数が多い
      isMounted.current &&
      (oldClientImageLibraryImagesCount() === 0 ||
        oldClientImageLibraryImagesCount() <= clientImages.length)
    )
      return;

    try {
      const { data } = await axios.get<ClientImageResponse | ApiErrorResponse>(
        apiUrls.clientImageUrl,
      );
      if (isApiError(data)) {
        sendErrorLog(new Error(data.message || "画像の取得に失敗しました"));
        return;
      }
      // FIXME: 旧ライブラリのDOMが消されたりすると、無限にAPIリクエストされてしまうのでここで制御
      if (data.data.length === clientImages.length) return;

      // IDの降順でソートし、最新の画像が一番上に来るようにする
      const newClientImages = data.data
        .sort((a, b) => b.id - a.id)
        .map((clientImage) => ({
          id: clientImage.id,
          filename: clientImage.name,
          imageUrl: clientImage.urls.minimum,
          squareImageUrl: clientImage.urls.medium,
        }));

      setClientImages(newClientImages);
      isMounted.current = true;
    } catch (error) {
      sendErrorLog(new Error(error instanceof Error ? error.message : "画像の取得に失敗しました"));
    }
  }, [apiUrls.clientImageUrl, setClientImages, clientImages]);

  useEffect(() => {
    // 求人原稿編集ページの「職種」に変更があったら、それに対応した画像カテゴリを取得する処理。初回求人作成時のみ「職種」の変更が可能
    const selectBusinessTypeId = document.getElementById(
      "work_work_contents_attributes_0_business_type_id",
    ) as HTMLSelectElement;
    const handleChangeBusinessType = (event: Event) => {
      const target = event.target as HTMLSelectElement;
      handleChangeAlbumCategoryOptions(target.value);
    };

    selectBusinessTypeId.addEventListener("change", handleChangeBusinessType);

    return () => {
      selectBusinessTypeId.removeEventListener("change", handleChangeBusinessType);
    };
  }, [handleChangeAlbumCategoryOptions]);

  return (
    <Presentation
      errorMessages={errorMessages}
      apiUrls={apiUrls}
      initialWorkContentAlbums={initialWorkContentAlbums}
      planAndImageInfoProps={planAndImageInfoProps}
      selectedWorkContentAlbums={selectedWorkContentAlbums}
      showModal={showModal}
      albumCategoryOptions={albumCategoryOptions}
      clientImages={clientImages}
      fetchClientImages={fetchClientImages}
      setClientImages={setClientImages}
      handleConfirmAlbumImages={handleConfirmAlbumImages}
      setShowModal={setShowModal}
      handleChangeAlbumSortNo={handleChangeAlbumSortNo}
      handleChangeAlbumCategory={handleChangeAlbumCategory}
      buttonProps={{ onClick: () => setShowModal(!showModal) }}
    />
  );
};
